import React, { useMemo, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import Swal from "sweetalert2";
import Badge from "../../../library/Badge";
import Banner from "../../../library/Banner";
import Button from "../../../library/Button";
import { confirmDialog } from "../../../library/confirmation";
import DropdownButton from "../../../library/DropdownButton";
import StickyFooter from "../../../library/StickyFooter";
import { exportTableData } from "../../../../utils/commonFunction";
import { DeleteProgrammeStudyPath, updateProgrammeStudyPath } from "../../../../services/ProgramService";
import {
    invalidateStudyPathQueries,
    useStudyPathPhaseList,
    useStudyPathUnassignedModules,
} from "../../../../query/hooks/QueryHooksStudyPaths";
import { useUnsavedChangesGuard } from "../../../../hooks/useUnsavedChangesGuard";
import {
    buildPhaseExportRows,
    getGroupForModuleType,
    getStudyPathModuleId,
    mergeVisibleUnassignedModules,
    MODULE_GROUPS,
    normalizeModuleRows,
    PHASE_EXPORT_HEADERS,
    sumModuleCredits,
} from "./studyPaths.utils";
import PhaseCardsColumn from "./PhaseCardsColumn";
import useStudyPathPermissions from "./useStudyPathPermissions";
import { useStagedPhaseModules } from "./useStagedPhaseModules";
import PhaseModuleGroupsTable from "./PhaseModuleGroupsTable";
import StudyPhasesToggle from "./StudyPhasesToggle";
import UnassignedModulesPanel from "./UnassignedModulesPanel";
import {
    StyledLockedBannerWrap,
    StyledMainColumn,
    StyledPageStack,
    StyledToolbarActions,
    StyledToolbarRow,
    StyledTwoColumnLayout,
} from "./studyPaths.styles";

/**
 * SPEC1 — Study Phases list: phase cards on the left, the selected phase's grouped modules and
 * the Unassigned Modules panel on the right.
 * @param {string | number} props.id PMC programme id.
 * @param {object} [props.versionData] Programme version record, for its `lock_version_settings`.
 */
function StudyPhasesList({ id: programId, versionData }) {
    const history = useHistory();
    const location = useLocation();
    const query = new URLSearchParams(location.search);
    const queryVersionId = query.get("versionId");
    const queryProgrammeId = query.get("programmeId") || programId;

    const [selectedPhaseId, setSelectedPhaseId] = useState(null);
    const isLocked = Boolean(versionData?.lock_version_settings);
    const disabledReason = isLocked ? "Unlock the version to make changes." : undefined;

    const { canAdd, canEdit, canDelete, canEditModules, noPermissionReason } = useStudyPathPermissions();
    const reasonFor = (granted) => (isLocked ? disabledReason : (granted ? undefined : noPermissionReason));

    const listRequestData = useMemo(() => ({
        programme_id: queryProgrammeId,
        version_id: queryVersionId,
        page: 1,
        limit: 100,
        key: "order_number",
        sort: "ASC",
    }), [queryProgrammeId, queryVersionId]);

    const { data: phaseListData, isLoading: phasesLoading, isError: phasesError } = useStudyPathPhaseList(listRequestData);
    const phases = phaseListData.data;
    const selectedPhase = phases.find((phase) => phase.id === selectedPhaseId) || phases[0];

    const hydrateFrom = selectedPhase
        ? {
            core: selectedPhase.core_module || [],
            foundation: selectedPhase.foundation_module || [],
            elective: selectedPhase.elective_module || [],
        }
        : null;

    const {
        stagedModules,
        stageModule,
        unstageModule,
        isDirty: modulesDirty,
        resetToBaseline,
        commitBaseline,
    } = useStagedPhaseModules({
        groups: MODULE_GROUPS,
        hydrateFrom,
        resetKey: selectedPhase?.id ?? null,
        getModuleId: getStudyPathModuleId,
    });

    const {
        data: unassignedModules,
        isLoading: unassignedLoading,
        isError: unassignedError,
    } = useStudyPathUnassignedModules(queryProgrammeId, queryVersionId);

    const visibleUnassignedModules = mergeVisibleUnassignedModules(unassignedModules, stagedModules, hydrateFrom, getStudyPathModuleId);

    const groupEntries = MODULE_GROUPS.map((group) => {
        const modules = stagedModules[group.key].map((module) => ({
            id: getStudyPathModuleId(module),
            module_id: getStudyPathModuleId(module),
            name: module.name,
            code: module.Code,
            duration: module.Duration,
            credits: module.credits,
        }));
        return {
            group,
            modules,
            suppliedCredits: sumModuleCredits(normalizeModuleRows(modules)),
        };
    });
    const requiredCreditsByGroup = Object.fromEntries(
        MODULE_GROUPS.map((group) => [group.key, selectedPhase?.[group.creditsField]])
    );

    const [isSavingModules, setIsSavingModules] = useState(false);

    useUnsavedChangesGuard(modulesDirty, true, history);

    const goTo = (type, extra = "") => {
        history.push(`/program-management-center/programmes/versions/study_path?type=${type}&versionId=${queryVersionId}&programmeId=${queryProgrammeId}${extra}`);
    };

    const confirmDiscardThen = (action) => {
        if (!modulesDirty) {
            action();
            return;
        }
        confirmDialog({
            title: "Discard changes?",
            text: "You have unsaved module changes for this phase. Discard them?",
            confirmButtonText: "Discard changes",
        }).then((confirmed) => {
            if (confirmed) {
                action();
            }
        });
    };

    const handleSaveModules = async () => {
        if (!selectedPhase) {
            return;
        }
        setIsSavingModules(true);
        try {
            await updateProgrammeStudyPath({
                id: selectedPhase.id,
                name: selectedPhase.name,
                programme_id: selectedPhase.programme_id,
                version_id: selectedPhase.version_id,
                year: selectedPhase.year,
                semester_term: selectedPhase.semester_term,
                core_credits: selectedPhase.core_credits,
                foundational_credits: selectedPhase.foundational_credits,
                elective_credits: selectedPhase.elective_credits,
                core_modules: stagedModules.core.map(getStudyPathModuleId),
                foundational_modules: stagedModules.foundation.map(getStudyPathModuleId),
                elective_modules: stagedModules.elective.map(getStudyPathModuleId),
            });
            commitBaseline();
            await invalidateStudyPathQueries(queryProgrammeId, queryVersionId, selectedPhase.id);
            Swal.fire("Saved!", "The phase's modules have been saved.", "success");
        } catch (error) {
            console.log("StudyPhasesList.js->handleSaveModules->Error", error);
            Swal.fire("Error", error?.response?.data?.message || "Something went wrong.", "error");
        } finally {
            setIsSavingModules(false);
        }
    };

    const handleDelete = (phase) => {
        Swal.fire({
            title: "Are you sure?",
            text: `Delete phase "${phase.name}"? This cannot be undone.`,
            icon: "warning",
            showCancelButton: true,
            confirmButtonText: "Yes, delete it!",
            customClass: { popup: "ami-confirm-dialog" },
        }).then(async (result) => {
            if (!result.isConfirmed) {
                return;
            }
            try {
                await DeleteProgrammeStudyPath({ id: phase.id });
                Swal.fire("Deleted!", "The phase has been removed.", "success");
                await invalidateStudyPathQueries(queryProgrammeId, queryVersionId, phase.id);
            } catch (error) {
                console.log("StudyPhasesList.js->handleDelete->Error", error);
                Swal.fire("Error", error?.response?.data?.message || "Something went wrong.", "error");
            }
        });
    };

    const handleAssignUnassignedModule = (module) => {
        if (!selectedPhase) {
            return;
        }
        const group = getGroupForModuleType(module.type);
        stageModule(group ? group.key : "core", module);
    };

    const handleUnlinkModule = (module) => unstageModule(module.id);

    const handleExport = (fileType) => {
        const rows = buildPhaseExportRows(phases);
        exportTableData(fileType, "Study Phases", PHASE_EXPORT_HEADERS, rows);
    };

    return (
        <StyledPageStack>
            {isLocked && (
                <StyledLockedBannerWrap>
                    <Banner tone="locked" badge={<Badge tone="warningSolid" icon="lock">Locked</Badge>}>
                        Study path phases are currently read-only. Creating, editing, re-arranging and
                        module assignment are disabled. Unlock the version to make changes.
                    </Banner>
                </StyledLockedBannerWrap>
            )}

            <StyledToolbarRow>
                <StudyPhasesToggle
                    mode="table"
                    onChange={(mode) => goTo(mode)}
                    rearrangeDisabled={!phasesLoading && phases.length === 0}
                    rearrangeDisabledReason="There aren't any study phases yet."
                />
                <StyledToolbarActions>
                    <DropdownButton
                        icon="download"
                        label="Export"
                        options={[
                            { key: "xlsx", icon: "file-excel", label: "Spreadsheet", onClick: () => handleExport("xlsx") },
                            { key: "csv", icon: "file-csv", label: "CSV", onClick: () => handleExport("csv") },
                            { key: "pdf", icon: "file-pdf", label: "PDF", onClick: () => handleExport("pdf") },
                        ]}
                    />
                    <Button
                        type="button"
                        onClick={() => goTo("create")}
                        disabled={isLocked || !canAdd}
                        title={reasonFor(canAdd)}
                    >
                        <i className="fal fa-plus" aria-hidden="true" /> Create New Phase
                    </Button>
                </StyledToolbarActions>
            </StyledToolbarRow>

            <StyledTwoColumnLayout>
                <PhaseCardsColumn
                    phases={phases}
                    loading={phasesLoading}
                    error={phasesError}
                    selectedPhaseId={selectedPhase?.id}
                    onSelect={(phase) => confirmDiscardThen(() => setSelectedPhaseId(phase.id))}
                    onEdit={(phase) => goTo("open", `&id=${phase.id}`)}
                    onDelete={handleDelete}
                    editDisabled={isLocked || !canEdit}
                    editDisabledReason={reasonFor(canEdit)}
                    deleteDisabled={isLocked || !canDelete}
                    deleteDisabledReason={reasonFor(canDelete)}
                />

                <StyledMainColumn>
                    {selectedPhase && (
                        <PhaseModuleGroupsTable
                            groups={groupEntries}
                            requiredCredits={requiredCreditsByGroup}
                            onOpenModule={(module) => window.open(`/program-management-center/modules/module/open/${module.id}`, "_blank")}
                            onUnlinkModule={(module) => handleUnlinkModule(module)}
                            assignDisabled={isLocked || !canEditModules}
                            assignDisabledReason={reasonFor(canEditModules)}
                            showGroupControls={false}
                            hideEmptyGroups
                            showPagination
                        />
                    )}

                    <UnassignedModulesPanel
                        modules={visibleUnassignedModules}
                        loading={unassignedLoading}
                        error={unassignedError}
                        onAssign={handleAssignUnassignedModule}
                        disabled={isLocked || !canEditModules || !selectedPhase}
                        disabledReason={!selectedPhase ? "Select a phase first." : reasonFor(canEditModules)}
                    />
                </StyledMainColumn>
            </StyledTwoColumnLayout>

            {selectedPhase && (
                <StickyFooter
                    onSave={handleSaveModules}
                    onCancel={resetToBaseline}
                    isLoading={isSavingModules}
                    saveDisabled={isLocked || !canEditModules || !modulesDirty}
                    cancelDisabled={!modulesDirty}
                    cancelTone="secondary"
                    saveText="Save Modules"
                    cancelText="Cancel"
                >
                    {modulesDirty && (
                        <span><i className="fas fa-exclamation-circle" aria-hidden="true" /> You have unsaved module changes</span>
                    )}
                </StickyFooter>
            )}
        </StyledPageStack>
    );
}

StudyPhasesList.displayName = "StudyPhasesList";

export default StudyPhasesList;
